import { useMemo, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { useDesignerApp } from '../../Contexts/DesignerAppContext';

/** Keys on the palette object that are not color groups and should be skipped. */
const PALETTE_EXCLUDED_KEYS = new Set(['mode', 'contrastThreshold', 'tonalOffset', 'getContrastText', 'augmentColor']);

/** Walk theme.palette and return all dot-notation color tokens (e.g. "primary.main", "divider"). */
function getPaletteColorTokens(theme: Theme): string[] {
	const tokens: string[] = [];
	const palette = theme.palette as Record<string, any>;

	for (const [key, value] of Object.entries(palette)) {
		if (PALETTE_EXCLUDED_KEYS.has(key)) continue;
		if (typeof value === 'string') {
			tokens.push(key);
		} else if (typeof value === 'object' && value !== null) {
			tokens.push(key);
			for (const [subKey, subValue] of Object.entries(value)) {
				if (typeof subValue === 'string') {
					tokens.push(`${key}.${subKey}`);
				}
			}
		}
	}

	return tokens;
}

type ColorInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	color: string | undefined;
	onColorChange: (color: string | undefined) => void;
};

const isHexColor = (value: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);

/** Resolve a MUI palette token like "primary.main" to a CSS color string. */
function resolveThemeColor(theme: Theme, token: string): string {
	const parts = token.split('.');
	let node: any = theme.palette;
	for (const part of parts) {
		node = node?.[part];
		if (node === undefined) return '';
	}
	return typeof node === 'string' ? node : '';
}

export function ColorInputDialog({ open, setOpen, color, onColorChange }: ColorInputDialogProps) {
	const { bundle } = useDesignerApp();
	const bundleTheme = useMemo(() => createTheme(bundle?.Theme ?? {}), [bundle?.Theme]);
	const colorOptions = useMemo(() => getPaletteColorTokens(bundleTheme), [bundleTheme]);
	const [localColor, setLocalColor] = useState(color || '');

	const hexColor = useMemo(() => {
		if (isHexColor(localColor)) {
			return localColor;
		}
		return '#000000';
	}, [localColor]);

	const handleSave = () => {
		const trimmed = localColor.trim();
		onColorChange(trimmed ? trimmed : undefined);
		setOpen(false);
	};

	const handleCancel = () => {
		setLocalColor(color || '');
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
			<DialogTitle>Configure Color</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Color value"
						value={localColor}
						onChange={(e) => setLocalColor(e.target.value)}
						fullWidth
						autoFocus
						placeholder="e.g. #1976d2, primary.main, rgb(255,0,0)"
						helperText="Supports MUI tokens, hex, rgb, and CSS color values"
					/>
					<TextField
						label="Hex picker"
						type="color"
						value={hexColor}
						onChange={(e) => setLocalColor(e.target.value)}
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
					<Box>
						<Typography variant="caption" color="text.secondary">
							Common theme colors
						</Typography>
						<Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
							{colorOptions.map((option) => {
								const resolved = resolveThemeColor(bundleTheme, option) || resolveThemeColor(bundleTheme, `${option}.main`);
								return (
									<Tooltip key={option} title={option}>
										<Button
											size="small"
											variant={localColor === option ? 'contained' : 'outlined'}
											onClick={() => setLocalColor(option)}
											sx={{ minWidth: 0, p: 0.5, gap: 0.75, justifyContent: 'flex-start', overflow: 'hidden' }}
										>
											<Box
												sx={{
													width: 14,
													height: 14,
													borderRadius: 0.5,
													border: '1px solid',
													borderColor: 'divider',
													backgroundColor: resolved || option,
													flexShrink: 0,
												}}
											/>
											<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
												{option}
											</Box>
										</Button>
									</Tooltip>
								);
							})}
						</Box>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setLocalColor('')}>Clear</Button>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
